// src/pages/academy/AcademyWritingForm.jsx
import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowRight, Save } from "lucide-react";
import { academyContentAPI, academyQuestionsAPI, getApiError } from "../../services/academyService";
import { ACADEMY_ROOT, DIFFICULTY_OPTIONS } from "./academyConstants";
import QuestionTextEditor from "./components/QuestionTextEditor";
import MediaDropField from "./components/MediaDropField";
import { findContentItem } from "./utils/findContentItem";

// الـ rich text editor بيرجّع "<p></p>" لما يكون فاضي، فنحوّله لنص فاضي قبل الإرسال
const isRichEmpty = (html) => !html || html.replace(/<[^>]*>/g, "").trim() === "";
const cleanRich = (html) => (isRichEmpty(html) ? "" : html);

// داتا قديمة اتخزنت نص عادي (من غير tags): نحوّلها لفقرات عشان الـ editor ما يدمجش الأسطر
const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const toHtml = (s) =>
  !s || /<[a-z][\s\S]*>/i.test(s)
    ? s || ""
    : s
        .split("\n")
        .map((l) => `<p>${escapeHtml(l)}</p>`)
        .join("");

export default function AcademyWritingForm({ skillId, itemId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(itemId);
  const backPath = `${ACADEMY_ROOT}/skills/${skillId}`;

  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    title: "",
    question_text: "",
    sample_answer: "",
    rubric: "",
    min_words: 50,
    max_words: 300,
    points: 10,
    pass_threshold: 60,
    difficulty: "MEDIUM",
    is_active: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [imageCleared, setImageCleared] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    const passed = location.state?.item;
    if (passed) {
      applyItem(passed);
      setInitLoading(false);
      return;
    }
    (async () => {
      try {
        // الباك إند بيقص page_size عند 100
        const data = await academyQuestionsAPI.getSkillQuestions(skillId, 1, 100);
        const found = findContentItem(data.questions || [], "writing", itemId);
        if (found) applyItem(found);
        else setErrors({ general: "تعذر تحميل بيانات السؤال" });
      } catch (err) {
        setErrors({ general: getApiError(err, "تعذر تحميل بيانات السؤال") });
      } finally {
        setInitLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, itemId]);

  const applyItem = (item) => {
    setForm((prev) => ({
      ...prev,
      title: item.title || "",
      question_text: item.question_text || "",
      sample_answer: toHtml(item.sample_answer),
      rubric: toHtml(item.rubric),
      min_words: item.min_words ?? prev.min_words,
      max_words: item.max_words ?? prev.max_words,
      points: item.points ?? prev.points,
      difficulty: item.difficulty || "MEDIUM",
      is_active: item.is_active ?? true,
      pass_threshold: item.pass_threshold ?? prev.pass_threshold,
    }));
    setExistingImageUrl(item.question_image || null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.title.trim()) newErrors.title = "العنوان مطلوب";
    if (isRichEmpty(form.question_text)) newErrors.question_text = "نص السؤال مطلوب";
    if (Number(form.max_words) <= Number(form.min_words))
      newErrors.max_words = "الحد الأقصى للكلمات يجب أن يكون أكبر من الحد الأدنى";
    if (form.pass_threshold < 0 || form.pass_threshold > 100)
      newErrors.pass_threshold = "لازم يكون بين 0 و 100";
    if (Object.keys(newErrors).length) return setErrors(newErrors);

    setLoading(true);
    setErrors({});
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("question_text", form.question_text);
      fd.append("sample_answer", cleanRich(form.sample_answer));
      fd.append("rubric", cleanRich(form.rubric));
      fd.append("min_words", form.min_words);
      fd.append("max_words", form.max_words);
      fd.append("points", form.points);
      fd.append("pass_threshold", form.pass_threshold);
      fd.append("difficulty", form.difficulty);
      fd.append("is_active", form.is_active ? "True" : "False");
      if (!isEdit) fd.append("academy_skill", skillId);
      if (imageFile) fd.append("question_image", imageFile);
      else if (isEdit && imageCleared) fd.append("question_image", "");

      if (isEdit) await academyContentAPI.update("writing", itemId, fd);
      else await academyContentAPI.create("writing", fd);
      navigate(backPath);
    } catch (err) {
      const data = err.response?.data || {};
      const fieldKeys = ["title", "question_text", "min_words", "max_words", "pass_threshold"];
      const hasFieldError = fieldKeys.some((k) => data[k]);
      setErrors(hasFieldError ? data : { ...data, general: getApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  if (initLoading) return <div className="max-w-xl mx-auto py-16 text-center text-gray-400">جارِ التحميل...</div>;

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to={backPath} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors">
          <ArrowRight className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">{isEdit ? "تعديل" : "إضافة"} سؤال كتابة</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 bg-white rounded-2xl border border-gray-200 p-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">العنوان <span className="text-red-500">*</span></label>
          <input name="title" value={form.title} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">نص السؤال <span className="text-red-500">*</span></label>
          <QuestionTextEditor value={form.question_text} onChange={(html) => setForm((p) => ({ ...p, question_text: html }))} />
          {errors.question_text && <p className="text-red-500 text-xs mt-1">{errors.question_text}</p>}
        </div>

        <MediaDropField label="صورة السؤال (اختياري)" kind="image" accept="image/*"
          file={imageFile} existingUrl={existingImageUrl} cleared={imageCleared}
          onChange={setImageFile} onClear={() => setImageCleared(true)} />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">إجابة نموذجية (اختياري)</label>
          <QuestionTextEditor
            value={form.sample_answer}
            onChange={(html) => setForm((p) => ({ ...p, sample_answer: html }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">معايير التصحيح (Rubric) (اختياري)</label>
          <QuestionTextEditor
            value={form.rubric}
            onChange={(html) => setForm((p) => ({ ...p, rubric: html }))}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">أقل عدد كلمات</label>
            <input type="number" name="min_words" value={form.min_words} onChange={handleChange} min={1}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            {errors.min_words && <p className="text-red-500 text-xs mt-1">{errors.min_words}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">أقصى عدد كلمات</label>
            <input type="number" name="max_words" value={form.max_words} onChange={handleChange} min={1}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            {errors.max_words && <p className="text-red-500 text-xs mt-1">{errors.max_words}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">النقاط</label>
            <input type="number" name="points" value={form.points} onChange={handleChange} min={1}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">حد النجاح % (Pass Threshold)</label>
            <input type="number" name="pass_threshold" value={form.pass_threshold} onChange={handleChange} min={0} max={100}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            {errors.pass_threshold && <p className="text-red-500 text-xs mt-1">{errors.pass_threshold}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">مستوى الصعوبة</label>
          <select name="difficulty" value={form.difficulty} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
            {DIFFICULTY_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
          نشط
        </label>

        {errors.general && <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">{errors.general}</p>}

        <button type="submit" disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white py-3 rounded-lg font-medium transition-colors">
          <Save className="w-5 h-5" />
          {loading ? "جارِ الحفظ..." : "حفظ"}
        </button>
      </form>
    </div>
  );
}